import Table from 'Components/Molecules/Table/Table';
import TableBody from 'Components/Molecules/TableBody/TableBody';
import TableCell from 'Components/Molecules/TableCell/TableCell';
import TableColumn from 'Components/Molecules/TableColumn/TableColumn';
import TableHeader from 'Components/Molecules/TableHeader/TableHeader';
import React, {useEffect, useMemo, useState} from 'react';
import Button from 'Components/Atoms/Button/Button';
import FontAwesomeIcon from 'Components/Atoms/FontAwesomeIcon/FontAwesomeIcon';
import {faPlus} from '@fortawesome/pro-solid-svg-icons';
import TableRow from 'Components/Molecules/TableRow/TableRow';
import {getAccessTokenFromLocal} from 'Services/helpers';
import {API_URL} from 'Store/Shared/Middleware/API/Constants';
import {useMutation, useQuery} from 'react-query';
import DeletionModal from 'Components/Molecules/Modals/Actions/DeletionModal/DeletionModal';
import ModalHeader from 'Components/Molecules/Modals/ModalHeader/ModalHeader';
import Modal from 'Components/Molecules/Modals/Modal/Modal';
import Container from 'Components/Atoms/Container/Container';
import FormSection from 'Components/Molecules/Form/FormSection/FormSection';
import TextField from 'Components/Atoms/Fields/TextField/TextField';
import FormGroup from 'Components/Molecules/Form/FormGroup/FormGroup';
import ModalFooter from 'Components/Molecules/Modals/ModalFooter/ModalFooter';
import ButtonGroup from 'Components/Molecules/ButtonGroup/ButtonGroup';
import Title from 'Components/Atoms/Title/Title';
import SelectField from 'Components/Molecules/Fields/SelectField/SelectField';
import StatusLabel from 'Components/Atoms/Label/StatusLabel/StatusLabel';
import styles from 'Components/Organisms/OptionList/OptionsOverview/OptionsOverview.module.sass';
import HTMLField from 'Pages/Dashboard/Settings/FieldsManagement/Fields/FieldTypes/HTMLField';
import Text from 'Components/Atoms/Text/Text';

export const optionListStatusMap = {
    'CONCEPT': 'Concept',
    'SEND': 'Verzonden',
    'AGREED': 'Akkoord',
    'REJECTED': 'Niet akkoord',
    'PROPOSED': 'Aangeboden'
}
export const optionListStatusColorMap = {
    'CONCEPT': 'passive',
    'SEND': 'passive',
    'AGREED': 'active',
    'REJECTED': 'inactive',
    'PROPOSED': 'active'
}

const Options = ({ option_list_id }: any) => {
    const [state, setState] = useState({
        isEditModalOpen: false,
        isCreateModalOpen: false,
        isDeleteModalOpen: false,
        deleteModalOptionId: ''
    });
    const [editOption, setEditOption] = useState<any>(null);

    const initialOption = useMemo(() => (
        {
            candidate_option_list_id: option_list_id,
            status: 'CONCEPT',
            company_name: '',
            url: '',
            description: ''
        }
    ), [option_list_id]);
    const [newOption, setNewOption] = useState(initialOption);

    const { data: options, refetch } = useQuery(
        ['options', option_list_id],
        async () => {
            const seachParams = new URLSearchParams();
            seachParams.set('filters[candidate_option_list_id]', option_list_id);

            const rawResponse = await fetch(`${API_URL}/api/v2.9/candidates/options?${seachParams.toString()}`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${getAccessTokenFromLocal()}`
                }
            });
            const response = await rawResponse.json();

            return response?.data;
        },
        {
            enabled: !!option_list_id
        }
    );

    useEffect(() => {
        refetch();
        setNewOption(initialOption);
    }, [option_list_id]);

    const {
        mutateAsync: updateOption,
        isLoading: isEditing
    } = useMutation(
        async (data: any) => {
            const rawResponse = await fetch(`${API_URL}/api/v2.9/candidates/options/${data.id}`, {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${getAccessTokenFromLocal()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });
            const response = await rawResponse.json();

            return response?.data;
        }
    );

    const processEdit = async () => {
        await updateOption(editOption);
        setState((state) => ({
            ...state,
            isEditModalOpen: false,
            editModalOptionId: ''
        }));
        refetch();
    }

    const handleEditClick = (option: any) => () => {
        setEditOption(option);
        setState((state) => ({
            ...state,
            isEditModalOpen: true
        }));
    }

    const cancelEdit = () => {
        setState((state) => ({
            ...state,
            isEditModalOpen: false,
            editModalOptionId: ''
        }));
    }

    const {
        mutateAsync: createOption,
        isLoading: isCreating
    } = useMutation(
        async (data: any) => {
            const rawResponse = await fetch(`${API_URL}/api/v2.9/candidates/options`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${getAccessTokenFromLocal()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });
            const response = await rawResponse.json();

            return response?.data;
        }
    );

    const handleCreateClick = () => setState((state) => ({
        ...state,
        isCreateModalOpen: true
    }));

    const cancelCreate = () => {
        setNewOption(initialOption);
        setState((state) => ({
            ...state,
            isCreateModalOpen: false
        }));
    };

    const processCreate = async () => {
        await createOption(newOption);
        setNewOption(initialOption);
        setState((state) => ({
            ...state,
            isCreateModalOpen: false
        }));
        refetch();
    }

    const {
        mutateAsync: deleteOption,
        isLoading: isDeleting
    } = useMutation(
        async (id: any) => {
            const rawResponse = await fetch(`${API_URL}/api/v2.9/candidates/options/${id}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${getAccessTokenFromLocal()}`
                }
            });
            const response = await rawResponse.json();

            return response?.data;
        }
    );

    const handleDeleteClick = (key: string) => () => setState((state) => ({
        ...state,
        isDeleteModalOpen: true,
        deleteModalOptionId: key
    }));

    const cancelDelete = () => setState((state) => ({
        ...state,
        isDeleteModalOpen: false,
        deleteModalOptionId: ''
    }));

    const processDelete = async () => {
        await deleteOption(state.deleteModalOptionId);
        setState((state) => ({
            ...state,
            isEditModalOpen: false,
            isDeleteModalOpen: false,
            deleteModalOptionId: ''
        }));
        refetch();
    }

    return <>
        <Table>
            <TableHeader>
                <TableColumn>Status</TableColumn>
                <TableColumn>Bedrijfsnaam</TableColumn>
                <TableColumn>Vacature URL</TableColumn>
                <TableColumn>Opmerking</TableColumn>
                <TableColumn>Verzonden door</TableColumn>
                <TableColumn>Verzonden op</TableColumn>
                <TableColumn>Aangemaakt door</TableColumn>
                <TableColumn>Aangemaakt op</TableColumn>
            </TableHeader>
            <TableBody>
                <TableRow>
                    <TableCell>
                        <Button
                            size='inline'
                            iconBefore={<FontAwesomeIcon icon={faPlus} />}
                            onClick={handleCreateClick}
                        >
                            Toevoegen
                        </Button>
                    </TableCell>
                </TableRow>
                {
                    options?.options?.map((option: any) => {
                        return <TableRow key={option.id} className={styles['test']} onClick={handleEditClick(option)}>
                            <TableCell>
                                <StatusLabel status={optionListStatusColorMap?.[option?.status]}>
                                    {optionListStatusMap?.[option?.status] || '-'}
                                </StatusLabel>
                            </TableCell>
                            <TableCell>
                                <Text>
                                    { option?.company_name || '-' }
                                </Text>
                            </TableCell>
                            <TableCell>
                                <Text tooltip={option?.url}>
                                    <a className={styles['link']} href={option?.url?.startsWith('https://') ? option?.url : `https://${option?.url}`} target={'_blank'}>
                                        { option?.url || '-' }
                                    </a>
                                </Text>
                            </TableCell>
                            <TableCell>
                                <Text>
                                    { option?.description }
                                </Text>
                            </TableCell>
                            <TableCell>{option?.sent_at || '-'}</TableCell>
                            <TableCell>{option?.sent_by || '-'}</TableCell>
                            <TableCell>{option?.created_at || '-'}</TableCell>
                            <TableCell>{option?.created_by || '-'}</TableCell>
                        </TableRow>
                    })
                }
            </TableBody>
        </Table>

        {
            state.isEditModalOpen
            && <Modal>
                <ModalHeader>
                    <Title size="large">Optie aanpassen</Title>
                </ModalHeader>
                <Container>
                    <div style={{height: '32px'}}/>
                    <FormSection>
                        <FormGroup title="Status">
                            <SelectField
                                isEdit={true}
                                value={editOption?.status}
                                onChange={(value) => setEditOption((editOption) => ({
                                    ...editOption,
                                    status: value
                                }))}
                                fieldProperties={{
                                    list: Object.keys(optionListStatusMap).map((item: any) => ({
                                        label: optionListStatusMap[item],
                                        value: item
                                    }))
                                }}
                            />
                        </FormGroup>
                        <FormGroup title="Bedrijfsnaam">
                            <TextField
                                placeholder="Bedrijfsnaam"
                                value={editOption?.company_name}
                                onChange={(value) => setEditOption((editOption) => ({
                                    ...editOption,
                                    company_name: value ?? ''
                                }))}
                            />
                        </FormGroup>
                        <FormGroup title="Vacature URL">
                            <TextField
                                placeholder="Vacature URL"
                                value={editOption?.url}
                                onChange={(value) => setEditOption((editOption) => ({
                                    ...editOption,
                                    url: value ?? ''
                                }))}
                            />
                        </FormGroup>
                        <FormGroup title="Opmerking">
                            <TextField
                                placeholder="Opmerking"
                                value={editOption?.description}
                                onChange={(value) => setEditOption((editOption) => ({
                                    ...editOption,
                                    description: value ?? ''
                                }))}
                            />
                        </FormGroup>
                    </FormSection>
                </Container>
                <ModalFooter info={
                    <Button
                        color='error'
                        variant="transparent"
                        onClick={handleDeleteClick(editOption?.id)}
                        isDisabled={isDeleting}
                        isLoading={isDeleting}
                    >
                        Verwijderen
                    </Button>
                }>
                    <ButtonGroup>
                        <Button onClick={cancelEdit}>
                            Annuleren
                        </Button>
                        <Button
                            color="primary"
                            variant="solid"
                            onClick={processEdit}
                            isDisabled={isEditing}
                            isLoading={isEditing}
                        >
                            Aanpassen
                        </Button>
                    </ButtonGroup>
                </ModalFooter>
            </Modal>
        }
        {
            state.isCreateModalOpen
            && <Modal>
                <ModalHeader>
                    <Title size="large">Voeg een nieuw optie toe</Title>
                </ModalHeader>
                <Container>
                    <div style={{height: '32px'}}/>
                    <FormSection>
                        <FormGroup title="Status">
                            <SelectField
                                isEdit={true}
                                value={newOption?.status}
                                onChange={(value) => setNewOption((newOption) => ({
                                    ...newOption,
                                    status: value
                                }))}
                                fieldProperties={{
                                    list: Object.keys(optionListStatusMap).map((item: any) => ({
                                        label: optionListStatusMap[item],
                                        value: item
                                    }))
                                }}
                            />
                        </FormGroup>
                        <FormGroup title="Bedrijfsnaam">
                            <TextField
                                placeholder="Bedrijfsnaam"
                                value={newOption?.company_name}
                                onChange={(value) => setNewOption((newOption) => ({
                                    ...newOption,
                                    company_name: value ?? ''
                                }))}
                            />
                        </FormGroup>
                        <FormGroup title="Vacature URL">
                            <TextField
                                placeholder="Vacature URL"
                                value={newOption?.url}
                                onChange={(value) => setNewOption((newOption) => ({
                                    ...newOption,
                                    url: value ?? ''
                                }))}
                            />
                        </FormGroup>
                        <FormGroup title="Opmerking">
                            <TextField
                                placeholder="Opmerking"
                                value={newOption?.description}
                                onChange={(value) => setNewOption((newOption) => ({
                                    ...newOption,
                                    description: value ?? ''
                                }))}
                            />
                        </FormGroup>
                    </FormSection>
                </Container>
                <ModalFooter>
                    <ButtonGroup>
                        <Button onClick={cancelCreate}>
                            Annuleren
                        </Button>
                        <Button
                            color="primary"
                            variant="solid"
                            onClick={processCreate}
                            isDisabled={isCreating}
                            isLoading={isCreating}
                        >
                            Aanmaken
                        </Button>
                    </ButtonGroup>
                </ModalFooter>
            </Modal>
        }
        {
            state.isDeleteModalOpen
            && <DeletionModal
                onClose={cancelDelete}
                onDelete={processDelete}
                isDeleting={isDeleting}
            />
        }
    </>
}

export default Options;